/**
 * Appointment Form Module - Simplified Version
 * Handles basic form logic, multi-step navigation, and date field population
 */

export default class AppointmentForm {
    constructor() {
        this.form = null;
        this.currentStep = 1;
        this.totalSteps = 2;

        this.init();
    }

    /**
     * Initialize appointment form if present on page
     */
    init() {
        this.form = document.getElementById('formAppointment');

        if (!this.form) {
            return; // No appointment form on this page
        }

        this.setupEventListeners();
        this.loadAppointmentData();
    }

    /**
     * Setup event listeners
     */
    setupEventListeners() {
        // Multi-step navigation buttons - only handle clicks within the appointment form
        document.addEventListener('click', (e) => {
            // Check if click is within the appointment form
            const clickedElement = e.target;
            const isWithinAppointmentForm = clickedElement.closest('#formAppointment') || clickedElement.closest('.form-appointmentapp');

            if (!isWithinAppointmentForm) {
                return; // Ignore clicks outside appointment form
            }

            if (e.target.classList.contains('next-step') || e.target.closest('.next-step')) {
                this.handleNextStep(e);
            }

            if (e.target.classList.contains('prev-step') || e.target.closest('.prev-step')) {
                this.handlePrevStep(e);
            }
        });

        // Form submission
        this.form.addEventListener('submit', (e) => {
            this.handleSubmit(e);
        });

        // Update confirmation when typing name
        const firstnameField = this.form.querySelector('input[name="personal.firstname"]');
        const lastnameField = this.form.querySelector('input[name="personal.lastname"]');

        if (firstnameField) {
            firstnameField.addEventListener('input', () => this.updateConfirmationName());
        }
        if (lastnameField) {
            lastnameField.addEventListener('input', () => this.updateConfirmationName());
        }
    }

    /**
     * Load appointment data from sessionStorage and fill form fields
     */
    loadAppointmentData() {
        const startDateTime = sessionStorage.getItem('appointmentStartDate');
        const endDateTime = sessionStorage.getItem('appointmentEndDate');

        if (!startDateTime || !endDateTime) {
            return; // No appointment data available
        }

        // Parse dates
        const startDate = new Date(startDateTime);
        const endDate = new Date(endDateTime);

        // Format for hidden fields
        const startDateFormatted = startDate.toISOString().split('T')[0]; // YYYY-MM-DD
        const endDateFormatted = endDate.toISOString().split('T')[0];
        const startTimeFormatted = startDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', hour12: false }); // HH:mm
        const endTimeFormatted = endDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', hour12: false });

        // Fill the 8 hidden fields using IDs and names
        this.setFieldValue('appointmentStartDate', startDateFormatted); // id
        this.setFieldValue('appointmentStartTime', startTimeFormatted); // id
        this.setFieldValue('appointmentEndDate', endDateFormatted); // id
        this.setFieldValue('appointmentEndTime', endTimeFormatted); // id
        this.setFieldValueByName('eventStartDate', startDateFormatted); // name
        this.setFieldValueByName('eventStartTime', startTimeFormatted); // name
        this.setFieldValueByName('eventEndDate', endDateFormatted); // name
        this.setFieldValueByName('eventEndTime', endTimeFormatted); // name

        // Update display elements
        this.updateDateTimeDisplay(startDate);
    }

    /**
     * Set field value by ID
     */
    setFieldValue(fieldId, value) {
        const field = document.getElementById(fieldId);
        if (field) {
            field.value = value;
        }
    }

    /**
     * Set field value by name attribute
     */
    setFieldValueByName(fieldName, value) {
        const field = this.form.querySelector(`input[name="${fieldName}"]`);
        if (field) {
            field.value = value;
        }
    }

    /**
     * Update date/time display elements in the page
     */
    updateDateTimeDisplay(startDate) {
        // Format date for display
        const formattedDate = startDate.toLocaleDateString('fr-FR', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });

        // Format time for display
        const formattedTime = startDate.toLocaleTimeString('fr-FR', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        });

        // Update all date display elements
        const dateElements = document.querySelectorAll('.js-appointment-date, .js-appointment-date-confirm, .js-appointment-date-final');
        dateElements.forEach(el => el.textContent = formattedDate);

        // Update all time display elements
        const timeElements = document.querySelectorAll('.js-appointment-time, .js-appointment-time-confirm, .js-appointment-time-final');
        timeElements.forEach(el => el.textContent = formattedTime);
    }

    /**
     * Handle next step button
     */
    handleNextStep(e) {
        e.preventDefault();

        // Simple validation: check required fields in current step within appointment form only
        const currentStepEl = this.form.querySelector(`[data-step="${this.currentStep}"]`);
        if (!currentStepEl) return;

        const requiredFields = currentStepEl.querySelectorAll('input[required], select[required], textarea[required]');
        let isValid = true;

        requiredFields.forEach(field => {
            if (!field.value.trim()) {
                field.classList.add('is-invalid');
                isValid = false;
            } else {
                field.classList.remove('is-invalid');
            }
        });

        if (!isValid) {
            return; // Stop if validation fails
        }

        // Move to next step
        if (this.currentStep < this.totalSteps) {
            this.showStep(this.currentStep + 1);

            // Update confirmation data when entering step 2
            if (this.currentStep === 2) {
                this.updateConfirmationData();
            }
        }
    }

    /**
     * Handle previous step button
     */
    handlePrevStep(e) {
        e.preventDefault();

        if (this.currentStep > 1) {
            this.showStep(this.currentStep - 1);
        }
    }

    /**
     * Show specific step within appointment form only
     */
    showStep(step) {
        // Hide all steps within appointment form only
        this.form.querySelectorAll('.step').forEach(stepEl => {
            stepEl.classList.add('d-none');
        });

        // Show target step within appointment form only
        const targetStep = this.form.querySelector(`[data-step="${step}"]`);
        if (targetStep) {
            targetStep.classList.remove('d-none');
        }

        this.currentStep = step;
    }

    /**
     * Update confirmation data (name, email, phone, dates)
     */
    updateConfirmationData() {
        // Get form values
        const firstname = this.form.querySelector('input[name="personal.firstname"]')?.value || '';
        const lastname = this.form.querySelector('input[name="personal.lastname"]')?.value || '';
        const email = this.form.querySelector('input[name="personal.email"]')?.value || '';
        const phone = this.form.querySelector('input[name="personal.phone"]')?.value || '';

        const fullName = `${firstname} ${lastname}`.trim();

        // Update name displays
        document.querySelectorAll('.js-name-people, .js-name-people-final').forEach(el => {
            el.textContent = fullName || '______';
        });

        // Update email display
        const emailEl = document.querySelector('.js-email-people');
        if (emailEl) emailEl.textContent = email || '______';

        // Update phone display
        const phoneEl = document.querySelector('.js-phone-people');
        if (phoneEl) phoneEl.textContent = phone || '______';

        // Update date/time in confirmation (already set in loadAppointmentData)
        const startDateTime = sessionStorage.getItem('appointmentStartDate');
        if (startDateTime) {
            const startDate = new Date(startDateTime);
            this.updateDateTimeDisplay(startDate);
        }
    }

    /**
     * Update confirmation name in real-time
     */
    updateConfirmationName() {
        const firstname = this.form.querySelector('input[name="personal.firstname"]')?.value || '';
        const lastname = this.form.querySelector('input[name="personal.lastname"]')?.value || '';
        const fullName = `${firstname} ${lastname}`.trim();

        document.querySelectorAll('.js-name-people, .js-name-people-final').forEach(el => {
            el.textContent = fullName || '______';
        });
    }

    /**
     * Handle form submission
     */
    handleSubmit(e) {
        // console.log('Form submission started...');

        // Check if appointment date/time is selected
        const startDateTime = sessionStorage.getItem('appointmentStartDate');
        const endDateTime = sessionStorage.getItem('appointmentEndDate');

        if (!startDateTime || !endDateTime) {
            e.preventDefault();
            alert('Veuillez sélectionner une date et heure de rendez-vous avant de soumettre le formulaire.');
            return false;
        }

        // Re-fill hidden fields just before submission (to be sure)
        this.loadAppointmentData();

        // Validate required fields
        const requiredFields = this.form.querySelectorAll('input[required], select[required], textarea[required]');
        let hasErrors = false;

        requiredFields.forEach(field => {
            if (!field.value.trim()) {
                field.classList.add('is-invalid');
                hasErrors = true;
            } else {
                field.classList.remove('is-invalid');
            }
        });

        if (hasErrors) {
            e.preventDefault();
            alert('Veuillez remplir tous les champs obligatoires.');
            return false;
        }

        // console.log('Form validation passed, submitting...');

        // Don't clear sessionStorage here - let the page redirect handle it
        // If submission fails, user can retry without re-selecting the date
        // If submission succeeds, the page will redirect and sessionStorage will be cleared naturally

        return true;
    }

    /**
     * Check if appointment form is available on current page
     */
    static isAvailable() {
        return document.getElementById('formAppointment') !== null ||
               document.querySelector('.form-appointmentapp') !== null;
    }
}
